'use client';

import { Stack, Theme, Typography } from '@mui/material';
import { withClarityTheme } from '@verifime/styles';
import { ReactElement } from 'react';
import { BrandedDotLoader } from '../BrandedDotLoader';
import { Logo } from '../Logo';

export type SigningYouInProps = {
  'data-testid'?: string;
};

/**
 * Design board screen 1b. The waiting screen a customer sees between pressing Continue on the inert
 * magic-link landing screen (MagicLinkContinue, board 1a) and the tokens landing, which is the one
 * moment in this flow where the one-time link is actually being spent.
 *
 * Two things follow from that, and both are why this screen exists rather than reusing
 * ValidatingSignInStatus. It must not be closed or reloaded: the fragment is still in the URL and
 * the record of a spent link is module state that dies with the page, so a refreshed tab re-detects
 * the link and offers Continue on a ticket that may already be gone, while closing mid-flight can
 * let the request succeed with the tokens never stored. That is what slot 08 says, and it is the
 * only copy on this screen the board marks as needing words. And it must read as this product's own
 * waiting screen rather than a generic status line, because a customer who has just pressed the one
 * button this flow offers should be able to see that the press landed.
 *
 * Rendered with no LoginContainer ancestor, unlike every other PasswordlessLogin state except
 * MAGIC_LINK_PENDING: see the comment at the top of PasswordlessLogin.tsx for why that container's
 * fixed width and card chrome are wrong for a board screen.
 *
 * The headline is a literal, not a copy slot: the board carries it as an existing live string, which
 * is what it already was here. The caption below inlines the approved copy directly rather than
 * taking it as a prop: it is a same-package constant, not data, so ADR-0050 is not in play.
 *
 * Wrapped in withClarityTheme for the same reason LinkSent and ExpiryClock are: this composition
 * reads theme.semantic.* and, through BrandedDotLoader, theme.palette.permanent.*, and it has no
 * control over the theme a future caller renders it under. Nesting is idempotent.
 */
function SigningYouInComponent({
  'data-testid': dataTestId,
}: Readonly<SigningYouInProps>): ReactElement {
  return (
    <Stack
      data-testid={dataTestId}
      role="status"
      aria-live="polite"
      sx={(theme: Theme) => ({
        width: '100%',
        boxSizing: 'border-box',
        alignItems: 'center',
        textAlign: 'center',
        // 24px where the board's own .stack sets 22px: the spacing scale steps 20 to 24 with
        // nothing between, and 24 is what MagicLinkContinue already uses for the same stack on the
        // screen this one follows. The whole 8 device px difference between the committed and the
        // reissued 1b reference is that 2px, twice. See this plan's decision D12.
        gap: theme.semantic.spacing['2lg'],
        marginInline: 'auto',
        maxWidth: ['100%', theme.semantic.size.sm],
        padding: theme.semantic.spacing['2lg'],
      })}
    >
      <Stack
        sx={(theme: Theme) => ({
          alignItems: 'center',
          gap: theme.semantic.spacing.base,
          paddingBlock: theme.semantic.spacing['2lg'],
        })}
      >
        <Logo layout="markOnly" size="large" colorMode="auto" />
        <BrandedDotLoader />
      </Stack>

      <Typography
        variant="h4"
        sx={(theme: Theme) => ({ fontWeight: theme.semantic.fontWeight.semibold })}
      >
        Signing you in ...
      </Typography>

      <Typography variant="body1">Do not close or refresh this page.</Typography>
    </Stack>
  );
}

export const SigningYouIn = withClarityTheme(SigningYouInComponent);
